import { memo } from "react";
import { TableCell, TableSortLabel, Tooltip } from "@mui/material";
import { FieldTitle, useTranslate, useResourceContext, useTranslateLabel, DatagridHeaderCellProps } from "react-admin";
import { styled } from "@mui/material/styles";
import clsx from "clsx";

// A custom header cell component for ra-Datagrid, which is cloned instance of ra-DatagridHeaderCell.
// It uses the custom DatagridHeaderCell component to render each column header with controlled label, tooltip and sorting functionality.
// source: https://github.com/marmelab/react-admin/blob/master/packages/ra-ui-materialui/src/list/datagrid/DatagridHeaderCell.tsx
export const DatagridHeaderCell = (props: DatagridHeaderCellProps): JSX.Element => {
  const { className, field, sort, updateSort, isSorting, ...rest } = props; // oxlint-disable-line no-unused-vars
  const resource = useResourceContext(props);
  const translate = useTranslate();
  const translateLabel = useTranslateLabel();

  if (!field) return <></>;

  // This is the main reason for creating this cloned copy from ra-DatagridHeaderCell.
  // To render a component label, and display its title in the tooltip.
  // APP-5682: avoid stringifying label component as object in the tooltip.
  const sortLabel = translate("ra.sort.sort_by", {
    field: translateLabel({
      label: typeof field.props.label === "string" ? field.props.label : (field.props.label?.props?.title ?? ""),
      resource,
      source: field.props.source,
    }),
    order: translate(`ra.sort.${sort.order === "ASC" ? "DESC" : "ASC"}`),
    _: translate("ra.action.sort"),
  });

  return (
    <StyledTableCell
      className={clsx(className, field.props.headerClassName)}
      align={field.props.textAlign || field.type.textAlign}
      variant="head"
      {...rest}
    >
      {updateSort &&
      field.props.sortable !== false &&
      field.type.sortable !== false &&
      (field.props.sortBy || field.props.source) ? (
        <Tooltip
          title={sortLabel}
          placement={
            field.props.textAlign === "right" || field.type.textAlign === "right" ? "bottom-end" : "bottom-start"
          }
          enterDelay={300}
        >
          <TableSortLabel
            active={sort.field === (field.props.sortBy || field.props.source)}
            direction={sort.order === "ASC" ? "asc" : "desc"}
            data-field={field.props.sortBy || field.props.source}
            data-order={field.props.sortByOrder || "ASC"}
            onClick={updateSort}
            classes={DatagridHeaderCellClasses}
          >
            <FieldTitle label={field.props.label} source={field.props.source} resource={resource} />
          </TableSortLabel>
        </Tooltip>
      ) : (
        <FieldTitle label={field.props.label} source={field.props.source} resource={resource} />
      )}
    </StyledTableCell>
  );
};

export default memo(
  DatagridHeaderCell,
  (props, nextProps) =>
    props.updateSort === nextProps.updateSort &&
    props.sort.field === nextProps.sort.field &&
    props.sort.order === nextProps.sort.order &&
    props.isSorting === nextProps.isSorting &&
    props.resource === nextProps.resource,
);

const PREFIX = "RaDatagridHeaderCell";

export const DatagridHeaderCellClasses = {
  icon: `${PREFIX}-icon`,
};

// Remove the sort icons when not active
const StyledTableCell = styled(TableCell, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})({
  [`& .MuiTableSortLabel-icon`]: {
    display: "none",
  },
  [`& .Mui-active .MuiTableSortLabel-icon`]: {
    display: "inline",
  },
});
